/**
 * BRSR AI Question Definitions
 *
 * Maps qualitative standalone textarea fields to their question text.
 * Each entry describes exactly one field path within the saved section data.
 *
 * Adding a new question:
 *   1. Find the InputTextarea in the section file.
 *   2. Copy its `value={...}` path → split into `stateKey` (top-level object)
 *      and `fieldPath` (dot-separated remainder).
 *   3. Copy the nearest `renderQuestionHeader` text as `question`.
 *   4. Append a new entry below. IDs must be unique.
 *
 * Sections supported: "A", "B", "C".
 *
 * If a new stateKey is added for Section C and that state key contains arrays,
 * add its default shape to SECTION_C_DEFAULTS below so that AI-partial saves
 * remain schema-complete (see applyBrsrAiAnswers for context).
 *
 * Section A stateKeys (csrData, operationsData) use spread-on-load in SectionA.js
 * so partial writes are safe — no SECTION_A_DEFAULTS needed.
 */

import { loadSectionData, saveSectionData } from "./answerManager";

// ---------------------------------------------------------------------------
// Question registry
// ---------------------------------------------------------------------------

export const BRSR_AI_QUESTIONS = [
  // ── Section A ─────────────────────────────────────────────────────────────
  {
    id: "a_operationsData_customerTypes",
    section: "A",
    stateKey: "operationsData",
    fieldPath: "customerTypes",
    question: "Provide a brief on types of customers.",
  },
  {
    id: "a_csrData_details",
    section: "A",
    stateKey: "csrData",
    fieldPath: "csrPolicyWebLink",
    question:
      "Whether CSR is applicable as per section 135 of Companies Act, 2013. If yes, provide details of the entity's Corporate Social Responsibility initiatives and policy.",
  },

  // ── Section B – Tab 1 ────────────────────────────────────────────────────
  {
    id: "b_tab1_q4",
    section: "B",
    stateKey: "tab1Data",
    fieldPath: "q4",
    fillMode: "perprinciple",
    question:
      "Name of the national and international codes / certifications / labels / standards (e.g. Forest Stewardship Council, Fairtrade, Rainforest Alliance, Trustea, SA 8000, OHSAS, ISO, BIS) adopted by your entity and mapped to each principle.",
  },
  {
    id: "b_tab1_q5",
    section: "B",
    stateKey: "tab1Data",
    fieldPath: "q5",
    fillMode: "perprinciple",
    question:
      "Specific commitments, goals and targets set by the entity with defined timelines, if any.",
  },
  {
    id: "b_tab1_q6",
    section: "B",
    stateKey: "tab1Data",
    fieldPath: "q6",
    fillMode: "perprinciple",
    question:
      "Performance of the entity against the specific commitments, goals and targets along-with reasons in case the same are not met.",
  },

  // ── Section B – Tab 2 ────────────────────────────────────────────────────
  {
    id: "b_tab2_q10_row1_review",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q10.row1.review",
    parentAnswerTypePath: "q10.row1",
    fillMode: "perprinciple",
    question:
      "Performance against above policies and follow up action – details of the committee / review body overseeing implementation.",
  },
  {
    id: "b_tab2_q10_row1_frequency",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q10.row1.frequency",
    parentAnswerTypePath: "q10.row1",
    fillMode: "perprinciple",
    question:
      "Performance against above policies and follow up action – frequency of review by the committee / governance body.",
  },
  {
    id: "b_tab2_q10_row2_review",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q10.row2.review",
    parentAnswerTypePath: "q10.row2",
    fillMode: "perprinciple",
    question:
      "Compliance with statutory requirements of relevance to the principles, and rectification of any non-compliances – details of the committee / review body.",
  },
  {
    id: "b_tab2_q10_row2_frequency",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q10.row2.frequency",
    parentAnswerTypePath: "q10.row2",
    fillMode: "perprinciple",
    question:
      "Compliance with statutory requirements of relevance to the principles, and rectification of any non-compliances – frequency of review.",
  },
  {
    id: "b_tab2_q12_row5",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q12.row5",
    fillMode: "perprinciple",
    question:
      "Any other reason for not implementing a principle (please specify).",
  },
  {
    id: "b_tab2_q7",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q7",
    question:
      "Statement by director responsible for the business responsibility report, highlighting ESG related challenges, targets and achievements.",
  },
  {
    id: "b_tab2_q8",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q8",
    question:
      "Details of the highest authority responsible for implementation and oversight of the Business Responsibility policy(ies).",
  },
  {
    id: "b_tab2_q9",
    section: "B",
    stateKey: "tab2Data",
    fieldPath: "q9",
    question:
      "Does the entity have a specified Committee of the Board / Director responsible for decision making on sustainability related issues? If yes, provide details.",
  },

  // ── Section C – Principle 1 Essential ────────────────────────────────────
  {
    id: "c_p1e_q4",
    section: "C",
    stateKey: "principle1Essential",
    fieldPath: "q4_antiCorruption",
    question:
      "Does the entity have an anti-corruption or anti-bribery policy? If yes, provide details in brief and if available, provide a web-link to the policy.",
  },
  {
    id: "c_p1e_q7",
    section: "C",
    stateKey: "principle1Essential",
    fieldPath: "q7_corrective",
    question:
      "Provide details of any corrective action taken or underway on issues related to fines / penalties / action taken by regulators/ law enforcement agencies/ judicial institutions, on cases of corruption and conflicts of interest.",
  },

  // ── Section C – Principle 1 Leadership ───────────────────────────────────
  {
    id: "c_p1l_q2",
    section: "C",
    stateKey: "principle1Leadership",
    fieldPath: "q2_conflictProcesses.details",
    question:
      "Whether there are processes for identifying and managing potential and actual conflict of interest of members of the Board? If yes, provide details of the process.",
  },

  // ── Section C – Principle 2 Essential ────────────────────────────────────
  {
    id: "c_p2e_q1_rd_details",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q1_rdCapex.rd.details",
    question:
      "Provide details of R&D investments made towards technologies to improve the environmental and social impacts of products and processes.",
  },
  {
    id: "c_p2e_q1_capex_details",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q1_rdCapex.capex.details",
    question:
      "Provide details of capital expenditure (capex) investments made towards technologies to improve the environmental and social impacts of products and processes.",
  },
  {
    id: "c_p2e_q3_plastics",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q3_reclaimProcesses.plastics",
    question:
      "Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for: Plastics (including packaging).",
  },
  {
    id: "c_p2e_q3_ewaste",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q3_reclaimProcesses.ewaste",
    question:
      "Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for: E-waste.",
  },
  {
    id: "c_p2e_q3_hazardous",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q3_reclaimProcesses.hazardous",
    question:
      "Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for: Hazardous waste.",
  },
  {
    id: "c_p2e_q3_other",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q3_reclaimProcesses.other",
    question:
      "Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for: Other waste.",
  },
  {
    id: "c_p2e_q4",
    section: "C",
    stateKey: "principle2Essential",
    fieldPath: "q4_epr",
    question:
      "Whether Extended Producer Responsibility (EPR) is applicable to the entity's activities. If yes, whether the waste collection plan is in line with the EPR plan submitted to Pollution Control Boards? If not, provide steps taken to address the same.",
  },

  // ── Section C – Principle 3 Essential ────────────────────────────────────
  {
    id: "c_p3e_q3",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q3_accessibility",
    question:
      "Accessibility of workplaces – Are the premises / offices of the entity accessible to differently abled employees and workers, as per the requirements of the Rights of Persons with Disabilities Act, 2016? If not, whether any steps are being taken by the entity in this regard.",
  },
  {
    id: "c_p3e_q4",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q4_equalOpportunity",
    question:
      "Does the entity have an equal opportunity policy as per the Rights of Persons with Disabilities Act, 2016? If so, provide a web-link to the policy.",
  },
  {
    id: "c_p3e_q10a",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q10_healthSafety.a_implemented.coverage",
    question:
      "Does the entity have an occupational health and safety management system? If yes, what is the coverage of such a system?",
  },
  {
    id: "c_p3e_q10b",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q10_healthSafety.b_processes",
    question:
      "What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?",
  },
  {
    id: "c_p3e_q10c",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q10_healthSafety.c_workerReportingDetails",
    question:
      "Whether you have processes for workers to report the work related hazards and to remove themselves from such risks? If yes, provide details.",
  },
  {
    id: "c_p3e_q10d",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q10_healthSafety.d_medicalAccessDetails",
    question:
      "Do the employees / workers of the entity have access to non-occupational medical and healthcare services? If yes, provide details.",
  },
  {
    id: "c_p3e_q12",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q12_safeWorkplace",
    question:
      "Describe the measures taken by the entity to ensure a safe and healthy work place.",
  },
  {
    id: "c_p3e_q15",
    section: "C",
    stateKey: "principle3Essential",
    fieldPath: "q15_correctiveAction",
    question:
      "Provide details of any corrective action taken or underway to address safety-related incidents (if any) and on significant risks / concerns arising from assessments of health & safety practices and working conditions.",
  },

  // ── Section C – Principle 3 Leadership ───────────────────────────────────
  {
    id: "c_p3l_q2",
    section: "C",
    stateKey: "principle3Leadership",
    fieldPath: "q2_statutoryDues",
    question:
      "Provide the measures undertaken by the entity to ensure that statutory dues have been deducted and deposited by the value chain partners.",
  },
  {
    id: "c_p3l_q6",
    section: "C",
    stateKey: "principle3Leadership",
    fieldPath: "q6_valueChainCorrective",
    question:
      "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from assessments of health and safety practices and working conditions of value chain partners.",
  },

  // ── Section C – Principle 4 Essential ────────────────────────────────────
  {
    id: "c_p4e_q1",
    section: "C",
    stateKey: "principle4Essential",
    fieldPath: "q1_identifyingStakeholders",
    question:
      "Describe the processes for identifying key stakeholder groups of the entity.",
  },

  // ── Section C – Principle 4 Leadership ───────────────────────────────────
  {
    id: "c_p4l_q1",
    section: "C",
    stateKey: "principle4Leadership",
    fieldPath: "q1_consultationProcesses",
    question:
      "Provide the processes for consultation between stakeholders and the Board on economic, environmental, and social topics or if consultation is delegated, how is feedback from such consultations provided to the Board.",
  },
  {
    id: "c_p4l_q2",
    section: "C",
    stateKey: "principle4Leadership",
    fieldPath: "q2_stakeholderConsultation.details",
    question:
      "Has the entity conducted any stakeholder consultation specifically to gather inputs on environmental and social topics? If so, provide details of the consultation and outcomes.",
  },
  {
    id: "c_p4l_q3",
    section: "C",
    stateKey: "principle4Leadership",
    fieldPath: "q3_vulnerableEngagement",
    question:
      "Provide details of instances of engagement with, and actions taken to, address the concerns of vulnerable / marginalized stakeholder groups.",
  },

  // ── Section C – Principle 5 Essential ────────────────────────────────────
  {
    id: "c_p5e_q4",
    section: "C",
    stateKey: "principle5Essential",
    fieldPath: "q4_focalPointDetails",
    question:
      "Details of the internal focal point(s) responsible for addressing human rights impacts or issues caused or contributed to by the business.",
  },
  {
    id: "c_p5e_q5",
    section: "C",
    stateKey: "principle5Essential",
    fieldPath: "q5_internalMechanisms",
    question:
      "Describe the internal mechanisms in place to redress grievances related to human rights issues.",
  },
  {
    id: "c_p5e_q8",
    section: "C",
    stateKey: "principle5Essential",
    fieldPath: "q8_preventAdverse",
    question:
      "Mechanisms to prevent adverse consequences to the complainant in discrimination and harassment cases.",
  },
  {
    id: "c_p5e_q9",
    section: "C",
    stateKey: "principle5Essential",
    fieldPath: "q9_businessAgreementsDetails",
    question:
      "Does the entity have a business process that incorporates human rights performance standards? If yes, provide details of how it monitors and evaluates compliance with these standards.",
  },
  {
    id: "c_p5e_q11",
    section: "C",
    stateKey: "principle5Essential",
    fieldPath: "q11_correctiveActions",
    question:
      "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 10 above.",
  },

  // ── Section C – Principle 5 Leadership ───────────────────────────────────
  {
    id: "c_p5l_q1",
    section: "C",
    stateKey: "principle5Leadership",
    fieldPath: "q1_businessProcessModifications",
    question:
      "Details of a business process being modified / introduced as a result of addressing human rights grievances/complaints.",
  },
  {
    id: "c_p5l_q2",
    section: "C",
    stateKey: "principle5Leadership",
    fieldPath: "q2_dueDiligenceScope",
    question:
      "Details of the scope and coverage of any Human rights due-diligence conducted.",
  },
  {
    id: "c_p5l_q3",
    section: "C",
    stateKey: "principle5Leadership",
    fieldPath: "q3_accessibilityVisitors",
    question:
      "Is the premise/office of the entity accessible to differently abled visitors, as per the requirements of the Rights of Persons with Disabilities Act, 2016? If not, whether any steps are being taken by the entity in this regard.",
  },
  {
    id: "c_p5l_q5",
    section: "C",
    stateKey: "principle5Leadership",
    fieldPath: "q5_correctiveActions",
    question:
      "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 4 above.",
  },

  // ── Section C – Principle 6 Essential ────────────────────────────────────
  {
    id: "c_p6e_q2",
    section: "C",
    stateKey: "principle6Essential",
    fieldPath: "q2_patScheme",
    question:
      "Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India? If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.",
  },
  {
    id: "c_p6e_q5",
    section: "C",
    stateKey: "principle6Essential",
    fieldPath: "q5_zeroLiquidDischarge",
    question:
      "Has the entity implemented a mechanism for Zero Liquid Discharge? If yes, provide details of its coverage and implementation.",
  },
  {
    id: "c_p6e_q8",
    section: "C",
    stateKey: "principle6Essential",
    fieldPath: "q8_ghgReductionProjects",
    question:
      "Does the entity have any project related to reducing Green House Gas emission? If yes, then provide details.",
  },
  {
    id: "c_p6e_q10",
    section: "C",
    stateKey: "principle6Essential",
    fieldPath: "q10_wasteManagementPractices",
    question:
      "Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.",
  },

  // ── Section C – Principle 6 Leadership ───────────────────────────────────
  {
    id: "c_p6l_q3",
    section: "C",
    stateKey: "principle6Leadership",
    fieldPath: "q3_biodiversityImpact",
    question:
      "With respect to the ecologically sensitive areas reported, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.",
  },
  {
    id: "c_p6l_q5",
    section: "C",
    stateKey: "principle6Leadership",
    fieldPath: "q5_businessContinuity",
    question:
      "Does the entity have a business continuity and disaster management plan? Give details in 100 words / web link.",
  },
  {
    id: "c_p6l_q6",
    section: "C",
    stateKey: "principle6Leadership",
    fieldPath: "q6_valueChainImpact",
    question:
      "Disclose any significant adverse impact to the environment, arising from the value chain of the entity. What mitigation or adaptation measures have been taken by the entity in this regard.",
  },
  {
    id: "c_p6l_q7",
    section: "C",
    stateKey: "principle6Leadership",
    fieldPath: "q7_valueChainAssessment",
    question:
      "Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.",
  },

  // ── Section C – Principle 8 Essential ────────────────────────────────────
  {
    id: "c_p8e_q3",
    section: "C",
    stateKey: "principle8Essential",
    fieldPath: "q3_grievanceMechanisms",
    question:
      "Describe the mechanisms to receive and redress grievances of the community.",
  },

  // ── Section C – Principle 8 Leadership ───────────────────────────────────
  {
    id: "c_p8l_q3b",
    section: "C",
    stateKey: "principle8Leadership",
    fieldPath: "q3b_marginalized",
    question:
      "From which marginalized / vulnerable groups does the entity procure?",
  },
  {
    id: "c_p8l_q3c",
    section: "C",
    stateKey: "principle8Leadership",
    fieldPath: "q3c_procurementPercentage",
    question:
      "What percentage of total procurement (by value) does procurement from marginalized / vulnerable groups constitute?",
  },

  // ── Section C – Principle 9 Essential ────────────────────────────────────
  {
    id: "c_p9e_q1",
    section: "C",
    stateKey: "principle9Essential",
    fieldPath: "q1_consumerComplaints",
    question:
      "Describe the mechanisms in place to receive and respond to consumer complaints and feedback.",
  },
  {
    id: "c_p9e_q5",
    section: "C",
    stateKey: "principle9Essential",
    fieldPath: "q5_cyberSecurityFramework",
    question:
      "Does the entity have a framework / policy on cyber security and risks related to data privacy? If available, provide a web-link of the policy.",
  },
  {
    id: "c_p9e_q6",
    section: "C",
    stateKey: "principle9Essential",
    fieldPath: "q6_correctiveActions",
    question:
      "Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.",
  },
  {
    id: "c_p9e_q7c",
    section: "C",
    stateKey: "principle9Essential",
    fieldPath: "q7c_dataBreachesImpact",
    question:
      "Impact, if any, of the data breaches on the entity and customers.",
  },

  // ── Section C – Principle 9 Leadership ───────────────────────────────────
  {
    id: "c_p9l_q1",
    section: "C",
    stateKey: "principle9Leadership",
    fieldPath: "q1_informationChannels",
    question:
      "Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).",
  },
  {
    id: "c_p9l_q2",
    section: "C",
    stateKey: "principle9Leadership",
    fieldPath: "q2_consumerEducation",
    question:
      "Steps taken to inform and educate consumers about safe and responsible usage of products and/or services.",
  },
  {
    id: "c_p9l_q3",
    section: "C",
    stateKey: "principle9Leadership",
    fieldPath: "q3_riskCommunication",
    question:
      "Mechanisms in place to inform consumers of any risk of disruption/discontinuation of essential services.",
  },
  {
    id: "c_p9l_q4",
    section: "C",
    stateKey: "principle9Leadership",
    fieldPath: "q4_productInfoSurveys",
    question:
      "Does the entity display product information on the product over and above what is mandated as per local laws? If yes, provide details in brief. Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole?",
  },
];

// ---------------------------------------------------------------------------
// Section C default state shapes (for AI-touched state keys only)
//
// When applyBrsrAiAnswers runs before a section is opened, there is no saved
// data for that state key. Without a fallback, setByPath starts from {} and
// stores a partial object — missing arrays crash SectionC on .map().
//
// These defaults must stay in sync with the useState() initialisers in
// SectionC.js for the four keys listed here.
//
// Principles 3-9 stateKeys added in this file (principle3Leadership,
// principle4Essential, etc.) contain only scalar string fields, so they do
// NOT need entries here — setByPath({}, "q1_identifyingStakeholders", val)
// produces a valid schema-complete object for those sections.
// ---------------------------------------------------------------------------

const SECTION_C_DEFAULTS = {
  // ── Principle 3 Leadership ──────────────────────────────────────────────
  principle3Leadership: {
    q1_lifeInsurance: { employees: "", workers: "" },
    q2_statutoryDues: "",
    q3_rehabilitation: {
      employees: { currentAffected: "", currentRehabilitated: "", previousAffected: "", previousRehabilitated: "" },
      workers: { currentAffected: "", currentRehabilitated: "", previousAffected: "", previousRehabilitated: "" },
    },
    q4_transitionAssistance: "",
    q5_valueChainAssessment: { healthSafety: "", workingConditions: "" },
    q6_valueChainCorrective: "",
  },

  // ── Principle 4 Essential ───────────────────────────────────────────────
  principle4Essential: {
    q1_identifyingStakeholders: "",
    q2_stakeholderGroups: [{ stakeholderGroup: "", vulnerableMarginalised: "", channels: "", frequency: "", purposeScope: "" }],
  },

  // ── Principle 4 Leadership ──────────────────────────────────────────────
  principle4Leadership: {
    q1_consultationProcesses: "",
    q2_stakeholderConsultation: { isUsed: "", details: "" },
    q3_vulnerableEngagement: "",
  },

  // ── Principle 5 Essential ───────────────────────────────────────────────
  principle5Essential: {
    q1_humanRightsTraining: {
      currentFY: {
        employees: { permanent: { total: "", covered: "" }, otherThanPermanent: { total: "", covered: "" } },
        workers: { permanent: { total: "", covered: "" }, otherThanPermanent: { total: "", covered: "" } },
      },
      previousFY: {
        employees: { permanent: { total: "", covered: "" }, otherThanPermanent: { total: "", covered: "" } },
        workers: { permanent: { total: "", covered: "" }, otherThanPermanent: { total: "", covered: "" } },
      },
    },
    q2_minimumWages: {
      employees: { currentFY: { total: "", belowMin: "", atMin: "" }, previousFY: { total: "", belowMin: "", atMin: "" } },
      workers: { currentFY: { total: "", belowMin: "", atMin: "" }, previousFY: { total: "", belowMin: "", atMin: "" } },
    },
    q3a_medianRemuneration: {
      bod: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      kmp: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      employees: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      workers: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
    },
    q3b_grossWagesFemales: { current: "", previous: "" },
    q4_focalPoint: "",
    q4_focalPointDetails: "",
    q5_internalMechanisms: "",
    q6_complaints: {
      sexualHarassment: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      discrimination: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      childLabour: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      forcedLabour: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      wages: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      otherHumanRights: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    },
    q7_poshComplaints: {
      totalComplaints: { current: "", previous: "" },
      percentageOfFemales: { current: "", previous: "" },
      upheld: { current: "", previous: "" },
    },
    q8_preventAdverse: "",
    q9_businessAgreements: "",
    q9_businessAgreementsDetails: "",
    q10_assessments: { childLabour: "", forcedLabour: "", sexualHarassment: "", discrimination: "", wages: "", others: "" },
    q11_correctiveActions: "",
  },

  // ── Principle 5 Leadership ──────────────────────────────────────────────
  principle5Leadership: {
    q1_businessProcessModifications: "",
    q2_dueDiligenceScope: "",
    q3_accessibilityVisitors: "",
    q4_valueChainAssessment: { sexualHarassment: "", discrimination: "", childLabour: "", forcedLabour: "", wages: "", others: "" },
    q5_correctiveActions: "",
  },

  // ── Principle 6 Essential ───────────────────────────────────────────────
  principle6Essential: {
    q1_energyConsumption: {
      renewable: {
        electricity: { current: "", previous: "" },
        fuel: { current: "", previous: "" },
        other: { current: "", previous: "" },
      },
      nonRenewable: {
        electricity: { current: "", previous: "" },
        fuel: { current: "", previous: "" },
        other: { current: "", previous: "" },
      },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q2_patScheme: "",
    q3_waterDetails: {
      withdrawal: {
        surfaceWater: { current: "", previous: "" },
        groundwater: { current: "", previous: "" },
        thirdParty: { current: "", previous: "" },
        seawater: { current: "", previous: "" },
        others: { current: "", previous: "" },
      },
      consumption: { current: "", previous: "" },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q4_waterDischarge: {
      surfaceWater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "", treatmentLevel: "" } },
      groundwater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "", treatmentLevel: "" } },
      seawater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "", treatmentLevel: "" } },
      thirdParties: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "", treatmentLevel: "" } },
      others: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "", treatmentLevel: "" } },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q5_zeroLiquidDischarge: "",
    q6_airEmissions: [
      { parameter: "NOx", unit: "", current: "", previous: "" },
      { parameter: "SOx", unit: "", current: "", previous: "" },
      { parameter: "Particulate matter (PM)", unit: "", current: "", previous: "" },
      { parameter: "Persistent organic pollutants (POP)", unit: "", current: "", previous: "" },
      { parameter: "Volatile organic compounds (VOC)", unit: "", current: "", previous: "" },
      { parameter: "Hazardous air pollutants (HAP)", unit: "", current: "", previous: "" },
      { parameter: "Others - please specify", unit: "", current: "", previous: "", specify: "" },
    ],
    q6_airEmissionsExternalAssessment: { hasAssessment: "", agencyName: "" },
    q7_ghgEmissions: {
      scope1: { current: "", previous: "", unit: "" },
      scope2: { current: "", previous: "", unit: "" },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q8_ghgReductionProjects: "",
    q9_wasteManagement: {
      totalWaste: {
        plastic: { current: "", previous: "" },
        ewaste: { current: "", previous: "" },
        biomedical: { current: "", previous: "" },
        construction: { current: "", previous: "" },
        battery: { current: "", previous: "" },
        radioactive: { current: "", previous: "" },
        otherHazardous: { current: "", previous: "", specify: "" },
        otherNonHazardous: { current: "", previous: "", specify: "" },
      },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      recovery: {
        recycled: { current: "", previous: "" },
        reused: { current: "", previous: "" },
        otherRecovery: { current: "", previous: "" },
      },
      disposal: {
        incineration: { current: "", previous: "" },
        landfilling: { current: "", previous: "" },
        otherDisposal: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q10_wasteManagementPractices: "",
    q11_ecologicallySensitive: [{ location: "", typeOfOperations: "", compliance: "" }],
    q12_eia: [{ projectName: "", eiaNotificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
    q13_environmentalCompliance: [{ law: "", nonComplianceDetails: "", finesPenalties: "", correctiveAction: "" }],
  },

  // ── Principle 6 Leadership ──────────────────────────────────────────────
  principle6Leadership: {
    q1_waterStress: {
      areaName: "",
      natureOfOperations: "",
      withdrawal: {
        surfaceWater: { current: "", previous: "" },
        groundwater: { current: "", previous: "" },
        thirdParty: { current: "", previous: "" },
        seawater: { current: "", previous: "" },
        others: { current: "", previous: "" },
      },
      consumption: { current: "", previous: "" },
      discharge: {
        surfaceWater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        groundwater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        seawater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        thirdParties: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        others: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
      },
    },
    q2_scope3Emissions: {
      totalScope3: { current: "", previous: "", unit: "" },
      intensity: { perRupeeTurnover: { current: "", previous: "" }, optional: { current: "", previous: "" } },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    q3_biodiversityImpact: "",
    q4_resourceEfficiency: [{ initiative: "", details: "", outcome: "" }],
    q5_businessContinuity: "",
    q6_valueChainImpact: "",
    q7_valueChainAssessment: "",
  },

  // ── Principle 8 Essential ───────────────────────────────────────────────
  principle8Essential: {
    q1_sia: [{ projectName: "", notificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
    q2_rrProjects: [{ projectName: "", state: "", district: "", pafNumber: "", pafPercentage: "", amountPaid: "" }],
    q3_grievanceMechanisms: "",
    q4_inputMaterial: { msme: { current: "", previous: "" }, withinIndia: { current: "", previous: "" } },
    q5_jobCreation: { rural: { current: "", previous: "" }, semiUrban: { current: "", previous: "" }, urban: { current: "", previous: "" }, metropolitan: { current: "", previous: "" } },
  },

  // ── Principle 8 Leadership ──────────────────────────────────────────────
  principle8Leadership: {
    q1_mitigateImpacts: [{ impact: "", correctiveAction: "" }],
    q2_csrAspirations: [{ state: "", district: "", amountSpent: "" }],
    q3a_preferentialPolicy: "",
    q3b_marginalized: "",
    q3c_procurementPercentage: "",
    q4_ipBenefits: [{ ipName: "", owned: "", benefitShared: "", basisCalculation: "" }],
    q5_ipDisputes: [{ authority: "", caseDetails: "", correctiveAction: "" }],
    q6_csrBeneficiaries: [{ csrProject: "", personsCount: "", vulnerablePercentage: "" }],
  },

  // ── Principle 9 Essential ───────────────────────────────────────────────
  principle9Essential: {
    q1_consumerComplaints: "",
    q2_productInfoTurnover: {
      dataPrivacy: "",
      advertising: "",
      recyclingSafeDisposal: "",
      safeResponsibleUsage: "",
      environmentalSocialParameters: "",
    },
    q3_complaintNumbers: {
      dataPrivacy: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      advertising: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      cyberSecurity: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      deliveryServices: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      restrictiveTraces: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      unfairTrade: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      other: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
    },
    q4_productRecalls: [
      { type: "Voluntary recalls", number: "", reasons: "" },
      { type: "Forced recalls", number: "", reasons: "" },
    ],
    q5_cyberSecurityFramework: "",
    q6_correctiveActions: "",
    q7a_dataBreachesInstances: "",
    q7b_dataBreachesPercentage: "",
    q7c_dataBreachesImpact: "",
  },

  // ── Principle 9 Leadership ──────────────────────────────────────────────
  principle9Leadership: {
    q1_informationChannels: "",
    q2_consumerEducation: "",
    q3_riskCommunication: "",
    q4_productInfoSurveys: "",
  },

  // ── Principle 1 Essential ───────────────────────────────────────────────
  principle1Essential: {
    q1_training: [
      { segment: "Board of Directors", programmes: "", topics: "", percentage: "" },
      { segment: "Key Managerial Personnel", programmes: "", topics: "", percentage: "" },
      { segment: "Employees other than BoD and KMPs", programmes: "", topics: "", percentage: "" },
      { segment: "Workers", programmes: "", topics: "", percentage: "" },
    ],
    q2_fines: {
      monetary: [
        { type: "Penalty/ Fine", principle: "", agency: "", amount: "", brief: "", appeal: "" },
        { type: "Settlement", principle: "", agency: "", amount: "", brief: "", appeal: "" },
        { type: "Compounding fee", principle: "", agency: "", amount: "", brief: "", appeal: "" },
      ],
      nonMonetary: [
        { type: "Imprisonment", principle: "", agency: "", brief: "", appeal: "" },
        { type: "Punishment", principle: "", agency: "", brief: "", appeal: "" },
      ],
    },
    q3_appeals: [{ caseDetails: "", agency: "" }],
    q4_antiCorruption: "",
    q5_disciplinary: {
      directors: { current: "", previous: "" },
      kmps: { current: "", previous: "" },
      employees: { current: "", previous: "" },
      workers: { current: "", previous: "" },
    },
    q6_conflict: {
      directors: { currentNumber: "", currentRemarks: "", previousNumber: "", previousRemarks: "" },
      kmps: { currentNumber: "", currentRemarks: "", previousNumber: "", previousRemarks: "" },
    },
    q7_corrective: "",
    q8_accountsPayable: { current: "", previous: "" },
    q9_openness: {
      concentrationPurchases: {
        a: { current: "", previous: "" },
        b: { current: "", previous: "" },
        c: { current: "", previous: "" },
      },
      concentrationSales: {
        a: { current: "", previous: "" },
        b: { current: "", previous: "" },
        c: { current: "", previous: "" },
      },
      shareRPTs: {
        a: { current: "", previous: "" },
        b: { current: "", previous: "" },
        c: { current: "", previous: "" },
        d: { current: "", previous: "" },
      },
    },
  },

  principle1Leadership: {
    q1_valueChainAwareness: [{ programmes: "", topics: "", percentage: "" }],
    q2_conflictProcesses: { hasProcesses: "", details: "" },
  },

  principle2Essential: {
    q1_rdCapex: {
      rd: { current: "", previous: "", details: "" },
      capex: { current: "", previous: "", details: "" },
    },
    q2_sustainableSourcing: { hasProcedures: "", percentage: "" },
    q3_reclaimProcesses: { plastics: "", ewaste: "", hazardous: "", other: "" },
    q4_epr: "",
  },

  principle3Essential: {
    q1a_employeeWellbeing: {
      permanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
      otherThanPermanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
    },
    q1b_workerWellbeing: {
      permanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
      otherThanPermanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
    },
    q1c_wellbeingSpending: { current: "", previous: "" },
    q2_retirementBenefits: {
      pf: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      gratuity: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      esi: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      others: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "", specify: "" },
    },
    q3_accessibility: "",
    q4_equalOpportunity: "",
    q5_returnRetention: {
      employees: { maleReturn: "", maleRetention: "", femaleReturn: "", femaleRetention: "" },
      workers: { maleReturn: "", maleRetention: "", femaleReturn: "", femaleRetention: "" },
    },
    q6_grievance: {
      permanentWorkers: { hasMechanism: "", details: "" },
      otherThanPermanentWorkers: { hasMechanism: "", details: "" },
      permanentEmployees: { hasMechanism: "", details: "" },
      otherThanPermanentEmployees: { hasMechanism: "", details: "" },
    },
    q7_membership: {
      currentFY: {
        permanentEmployees: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
        permanentWorkers: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
      },
      previousFY: {
        permanentEmployees: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
        permanentWorkers: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
      },
    },
    q8_training: {
      currentFY: {
        employees: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
        workers: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
      },
      previousFY: {
        employees: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
        workers: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
      },
    },
    q9_performanceReviews: {
      currentFY: {
        employees: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
        workers: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
      },
      previousFY: {
        employees: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
        workers: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
      },
    },
    q10_healthSafety: {
      a_implemented: { hasSystem: "", coverage: "" },
      b_processes: "",
      c_workerReporting: "",
      c_workerReportingDetails: "",
      d_medicalAccess: "",
      d_medicalAccessDetails: "",
    },
    q11_safetyIncidents: {
      ltifr: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      recordableInjuries: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      fatalities: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      highConsequence: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
    },
    q12_safeWorkplace: "",
    q13_complaints: {
      workingConditions: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      healthSafety: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    },
    q14_assessments: { healthSafety: "", workingConditions: "" },
    q15_correctiveAction: "",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read a nested value from an object using a dot-separated path. */
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

/** Return a new object with a nested value set at a dot-separated path. */
const setByPath = (obj, path, value) => {
  const keys = path.split(".");
  const result = { ...obj };
  let cursor = result;
  for (let i = 0; i < keys.length - 1; i++) {
    cursor[keys[i]] = { ...cursor[keys[i]] };
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return result;
};

// ---------------------------------------------------------------------------
// BRSR Principles (P1–P9) used to generate per-principle AI questions
// ---------------------------------------------------------------------------

const BRSR_PRINCIPLES = [
  "P1 – Ethics, Transparency and Accountability",
  "P2 – Sustainable and Safe Products/Services",
  "P3 – Employee Wellbeing",
  "P4 – Stakeholder Responsiveness",
  "P5 – Human Rights",
  "P6 – Environmental Protection",
  "P7 – Responsible Policy Engagement",
  "P8 – Inclusive Growth",
  "P9 – Consumer Value and Responsibility",
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Extract questions from saved section data.
 * Returns only questions where the current field value is empty / blank
 * (i.e. questions that genuinely need AI to fill in).
 *
 * For fillMode "perprinciple" questions, expands into 9 entries (one per
 * principle) so the AI can answer each principle independently. Only slots
 * that are currently empty are included (unless includeAnswered is true).
 *
 * @param {string} reportId
 * @param {boolean} includeAnswered  Set true to include already-answered fields.
 * @returns {Array<{id, question, section, stateKey, fieldPath, currentValue, principleIndex?}>}
 */
export const extractBrsrAiQuestions = (reportId, includeAnswered = false, section = null) => {
  const sectionA = loadSectionData(reportId, "A") || {};
  const sectionB = loadSectionData(reportId, "B") || {};
  const sectionC = loadSectionData(reportId, "C") || {};

  const getSectionData = (sec) => {
    if (sec === "A") return sectionA;
    if (sec === "B") return sectionB;
    return sectionC;
  };

  return BRSR_AI_QUESTIONS
    .flatMap((def) => {
      const sectionData = getSectionData(def.section);
      const topLevel = sectionData[def.stateKey] || {};

      if (def.fillMode === "perprinciple") {
        const parentObj = getByPath(topLevel, def.fieldPath) || {};
        const commonValue = parentObj.answerType === "common" ? (parentObj.commonValue?.trim() || "") : "";
        if (commonValue) {
          // Field answered as common text: skip when not including answered;
          // when including answered, surface commonValue so callers detect it as filled.
          if (!includeAnswered) return [];
          return BRSR_PRINCIPLES.map((principle, idx) => ({
            ...def,
            principleIndex: idx,
            question: `${def.question} (Answer specifically for ${principle})`,
            currentValue: commonValue,
          }));
        }
        const ppValues = Array.isArray(parentObj.perPrincipleValues) ? parentObj.perPrincipleValues : [];
        return BRSR_PRINCIPLES.map((principle, idx) => ({
          ...def,
          principleIndex: idx,
          question: `${def.question} (Answer specifically for ${principle})`,
          currentValue: ppValues[idx]?.trim() || "",
        }));
      }

      let currentValue = "";
      if (def.fillMode === "common") {
        const parentObj = getByPath(topLevel, def.fieldPath) || {};
        currentValue = parentObj.commonValue || "";
      } else {
        currentValue = getByPath(topLevel, def.fieldPath) || "";
      }
      return [{ ...def, currentValue }];
    })
    .filter((q) => includeAnswered || !q.currentValue.trim())
    .filter((q) => !section || q.section === section);
};

// Strip question echo: Gemini sometimes repeats the question text at the start of its answer.
// Tries full question match first, then base question (without the "(Answer specifically for ...)" suffix).
const stripQuestionEcho = (answer, questionText) => {
  if (!answer || !questionText) return answer;
  const a = answer.trim();
  const candidates = [
    questionText.trim(),
    questionText.trim().replace(/\s*\(Answer specifically for [^)]+\)\s*$/, "").trim(),
  ];
  for (const q of candidates) {
    if (q && a.toLowerCase().startsWith(q.toLowerCase())) {
      return a.slice(q.length).replace(/^[\s:.\-–]+/, "").trim();
    }
  }
  return a;
};

// Strip markdown formatting so AI text displays cleanly in plain textareas.
// Exported so SectionB (display) and applyBrsrAiAnswers (save) share one implementation.
export const stripMarkdown = (text) => {
  if (typeof text !== "string") return text ?? "";
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")    // **bold** — [^*]+ avoids matching bare asterisks
    .replace(/(?<![_\w])__([^_]+)__(?![_\w])/g, "$1")  // __bold__
    .replace(/\*([^*\n]+)\*/g, "$1")       // *italic* — [^*\n]+ avoids math/paths
    .replace(/(?<![_\w])_([^_\n]+)_(?![_\w])/g, "$1")  // _italic_
    .replace(/^#{1,6}\s+/gm, "")           // ### headings
    .replace(/^\s*[-*+]\s+/gm, "")         // bullet lists
    .trim();
};

/**
 * Apply AI-generated answers back into saved section data.
 *
 * Matches by INDEX — `sentQuestions[i]` maps to `results[i]`.
 * This is robust against EcoUtil normalising/rewriting question text on the
 * round-trip. Never relies on string equality of question text.
 *
 * @param {string} reportId
 * @param {Array}  sentQuestions  The array returned by extractBrsrAiQuestions()
 *                                that was sent to the API (same order).
 * @param {Array}  results        API response results array (same order).
 * @returns {{ written: number, totalFields: number }}
 *   written     – fields that received an answer
 *   totalFields – unique fields that were sent (for computing skipped count)
 */
export const applyBrsrAiAnswers = (reportId, sentQuestions, results) => {
  const sectionA = loadSectionData(reportId, "A") || {};
  const sectionB = loadSectionData(reportId, "B") || {};
  const sectionC = loadSectionData(reportId, "C") || {};

  let written = 0;
  let wroteToA = false;
  let wroteToB = false;
  let wroteToC = false;

  // Collect per-principle answers grouped by field before writing, so we can
  // decide common vs perprinciple after seeing all 9 answers for a field.
  const perprincipleGroups = {}; // key: "section:stateKey:fieldPath"

  results.forEach((result, i) => {
    if (!result.AIGenerated || !result.answer) return;

    const def = sentQuestions[i];
    if (!def) return; // guard: more results than questions sent

    const answer = stripMarkdown(result.answer);
    // Backend returns this literal string when it cannot extract an answer — treat as no answer.
    if (!answer || answer === "Could not parse answer from response.") return;

    // Per-principle questions are collected and applied as a group below.
    if (def.principleIndex !== undefined) {
      const key = `${def.section}:${def.stateKey}:${def.fieldPath}`;
      if (!perprincipleGroups[key]) {
        perprincipleGroups[key] = { def, answers: Array(9).fill(null) };
      }
      perprincipleGroups[key].answers[def.principleIndex] = stripQuestionEcho(answer, def.question);
      return;
    }

    let targetData;
    if (def.section === "A") targetData = sectionA;
    else if (def.section === "B") targetData = sectionB;
    else targetData = sectionC;

    // Fall back to the canonical default shape when the section has never been
    // opened (no saved data). This ensures the stored object is always
    // schema-complete so SectionC never receives a partial state on load.
    // Section A stateKeys (csrData, operationsData) use spread-on-load so
    // partial objects are safe — no SECTION_A_DEFAULTS needed.
    const topLevel = targetData[def.stateKey]
      || (def.section === "C" ? SECTION_C_DEFAULTS[def.stateKey] : null)
      || {};
    let updated;
    if (def.fillMode === "common") {
      let obj = setByPath(topLevel, `${def.fieldPath}.commonValue`, answer);
      obj = setByPath(obj, `${def.fieldPath}.answerType`, "common");
      updated = obj;
    } else {
      updated = setByPath(topLevel, def.fieldPath, answer);
    }

    if (def.section === "A") {
      sectionA[def.stateKey] = updated;
      wroteToA = true;
    } else if (def.section === "B") {
      sectionB[def.stateKey] = updated;
      wroteToB = true;
    } else {
      sectionC[def.stateKey] = updated;
      wroteToC = true;
    }
    written++;
  });

  // Apply grouped per-principle answers: if all answers are identical use
  // common mode, otherwise store individually in perprinciple mode.
  for (const { def, answers } of Object.values(perprincipleGroups)) {
    const filledAnswers = answers.filter(Boolean);
    if (filledAnswers.length === 0) continue;

    let targetData;
    if (def.section === "A") targetData = sectionA;
    else if (def.section === "B") targetData = sectionB;
    else targetData = sectionC;

    const topLevel = targetData[def.stateKey]
      || (def.section === "C" ? SECTION_C_DEFAULTS[def.stateKey] : null)
      || {};

    // Merge new answers into existing perPrincipleValues to preserve any
    // slots that were already filled and not re-sent to the AI.
    const parentObj = getByPath(topLevel, def.fieldPath) || {};
    const existingValues = Array.isArray(parentObj.perPrincipleValues)
      ? [...parentObj.perPrincipleValues]
      : Array(9).fill("");
    answers.forEach((a, idx) => { if (a !== null) existingValues[idx] = a; });

    const allSame = existingValues.every((v) => (v || "") === (existingValues[0] || "")) && existingValues[0];
    const resolvedType = allSame ? "common" : "perprinciple";
    let updated;
    if (allSame) {
      let obj = setByPath(topLevel, `${def.fieldPath}.commonValue`, existingValues[0]);
      obj = setByPath(obj, `${def.fieldPath}.answerType`, "common");
      updated = obj;
    } else {
      let obj = setByPath(topLevel, `${def.fieldPath}.perPrincipleValues`, existingValues);
      obj = setByPath(obj, `${def.fieldPath}.answerType`, "perprinciple");
      updated = obj;
    }
    // For fields nested inside a row (e.g. q10.row1.review), the row-level
    // answerType controls which render path the UI uses — update it too.
    if (def.parentAnswerTypePath) {
      updated = setByPath(updated, `${def.parentAnswerTypePath}.answerType`, resolvedType);
    }

    targetData[def.stateKey] = updated;
    if (def.section === "A") wroteToA = true;
    else if (def.section === "B") wroteToB = true;
    else wroteToC = true;
    written++;
  }

  // Only save sections we actually wrote to, not every loaded section.
  if (wroteToA) saveSectionData(reportId, "A", sectionA);
  if (wroteToB) saveSectionData(reportId, "B", sectionB);
  if (wroteToC) saveSectionData(reportId, "C", sectionC);

  // totalFields = unique fields sent: each perprinciple group counts as 1,
  // each non-perprinciple question counts as 1.
  const perprincipleFieldCount = Object.keys(perprincipleGroups).length;
  const plainFieldCount = sentQuestions.filter((q) => q.principleIndex === undefined).length;
  const totalFields = perprincipleFieldCount + plainFieldCount;

  return { written, totalFields };
};
